<template>
  <div class="sdl">
    <div v-if="loading" class="sdl-loading">
      <span class="sdl-spinner" aria-label="Loading your learning"></span>
    </div>

    <template v-else>
      <!-- ── My Pathways ─────────────────────────────────────────── -->
      <section class="sdl-section">
        <div v-if="pathways.length === 0" class="sdl-empty">
          No pathways have been assigned to you yet.
        </div>

        <div v-else class="sdl-pathways">
          <article v-for="p in pathways" :key="'pw-' + p.id" class="sdl-pathway" :class="{ 'is-open': isExpanded(p.id) }">
            <button type="button" class="sdl-pathway__head" :aria-expanded="isExpanded(p.id) ? 'true' : 'false'" @click="toggle(p.id)">
              <div class="sdl-pathway__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
              </div>
              <div class="sdl-pathway__main">
                <div class="sdl-pathway__titlerow">
                  <span class="sdl-kind">Pathway</span>
                  <h3 class="sdl-pathway__title">{{ p.title }}</h3>
                  <span :class="['sdl-pill', pathwayPillClass(p)]">{{ pathwayLabel(p) }}</span>
                </div>
                <p v-if="p.subtitle" class="sdl-pathway__sub">{{ p.subtitle }}</p>
                <div class="sdl-progress">
                  <div class="sdl-progress__bar"><span class="sdl-progress__fill" :style="{ width: p.progress_pct + '%' }"></span></div>
                  <span class="sdl-progress__text">{{ p.completed_count }} / {{ p.module_count }} modules · {{ p.progress_pct }}%</span>
                </div>
              </div>
              <svg class="sdl-pathway__chev" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </button>

            <div v-show="isExpanded(p.id)" class="sdl-modules">
              <div v-if="p.modules.length === 0" class="sdl-empty sdl-empty--inline">This pathway has no modules yet.</div>
              <ol v-else class="sdl-module-list">
                <li v-for="(m, idx) in p.modules" :key="'m-' + m.id" class="sdl-module"
                    :class="['sdl-module--' + m.module_status, { 'sdl-module--retired': m.removed_from_pathway }]">
                  <span class="sdl-module__num" aria-hidden="true">
                    <svg v-if="m.module_status === 'completed'" viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd"/></svg>
                    <svg v-else-if="m.module_status === 'locked'" viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/></svg>
                    <template v-else>{{ idx + 1 }}</template>
                  </span>
                  <div class="sdl-module__body">
                    <div class="sdl-module__name">{{ m.course_name || ('Module ' + (idx + 1)) }}</div>
                    <div class="sdl-module__meta">
                      <span>{{ moduleLabel(m) }}</span>
                      <span v-if="m.course_length" class="sdl-dot">·</span>
                      <span v-if="m.course_length">{{ m.course_length }} min</span>
                      <!-- The admin took this course out of the pathway
                           after it was completed. It stays on the learner's
                           record; it is simply no longer required. -->
                      <span v-if="m.removed_from_pathway" class="sdl-module__hint ignore-translation notranslate" translate="no">No longer required in this pathway</span>
                      <span v-else-if="m.module_status === 'locked' && m.gating_rule === 'unlock_on_date' && m.unlock_at" class="sdl-module__hint">Unlocks {{ formatDate(m.unlock_at) }}</span>
                      <span v-else-if="m.module_status === 'locked'" class="sdl-module__hint">Complete the previous module first</span>
                    </div>
                  </div>
                  <div class="sdl-module__actions">
                    <button v-if="m.module_status === 'in_progress'" type="button" class="sdl-btn sdl-btn--primary sdl-btn--sm" @click="openCourse(m.course_id)">Continue</button>
                    <button v-else-if="m.module_status === 'completed'" type="button" class="sdl-btn sdl-btn--sm" @click="openCourse(m.course_id)">Review</button>
                  </div>
                </li>
              </ol>
              <div class="sdl-modules__foot">
                <router-link :to="'/safedig/pathways/' + p.id" class="sdl-link">Open pathway →</router-link>
              </div>
            </div>
          </article>
        </div>
      </section>

    </template>
  </div>
</template>

<script>
/**
 * The learner's pathways, each with its modules and progress. Fed by
 * GET safedig/my-learning; the standalone-course half of that payload is
 * listed below this, on My Courses & Pathways.
 *
 * Emits `loaded` with the number of pathways found, so the page around it can
 * leave the section out when the learner has none.
 */
export default {
  name: 'SafeDigMyLearning',
  data() {
    return {
      loading: true,
      pathways: [],
      expanded: {},
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    fetch() {
      this.loading = true;
      this.$http.get('safedig/my-learning')
        .then(resp => {
          this.pathways = (resp.data && resp.data.pathways) || [];
          // My Courses & Pathways hides its Pathways section when there are none.
          this.$emit('loaded', this.pathways.length);
          // Open in-progress pathways by default so modules are visible at once.
          const open = {};
          this.pathways.forEach(p => { if (p.pathway_status !== 'completed') open[p.id] = true; });
          this.expanded = open;
        })
        .catch(() => { this.pathways = []; this.$emit('loaded', 0); })
        .finally(() => { this.loading = false; });
    },
    isExpanded(id) { return !!this.expanded[id]; },
    toggle(id) { this.$set(this.expanded, id, !this.expanded[id]); },
    openCourse(courseId) {
      if (!courseId) return;
      this.$router.push({ path: '/course_instructions', query: { id: courseId } });
    },
    pathwayLabel(p) {
      if (p.pathway_status === 'completed') return 'Completed';
      if (p.completed_count > 0) return 'In progress';
      return 'Not started';
    },
    pathwayPillClass(p) {
      return {
        'sdl-pill--green': p.pathway_status === 'completed',
        'sdl-pill--blue':  p.pathway_status !== 'completed' && p.completed_count > 0,
        'sdl-pill--gray':  p.pathway_status !== 'completed' && p.completed_count === 0,
      };
    },
    moduleLabel(m) {
      return { completed: 'Completed', in_progress: 'In progress', locked: 'Locked' }[m.module_status] || m.module_status;
    },
    formatDate(v) {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return v;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.sdl {
  --sd-accent: #3e7799;
  --sd-accent-dark: #2d5a73;
  --sd-accent-soft: rgba(62, 119, 153, 0.14);
  --sd-navy: #1e3a5f;
  --sd-text: #0f172a;
  --sd-muted: #475569;
  --sd-border: #e2e8f0;
  --sd-radius: 12px;
  --sd-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 6px 16px rgba(15, 23, 42, 0.05);
}
.sdl-section { margin-bottom: 2rem; }
.sdl-section__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 0.9rem; }
.sdl-section__title { font-size: 1.15rem; font-weight: 700; color: var(--sd-navy); margin: 0 0 0.2rem; }
.sdl-section__sub { font-size: 0.85rem; color: var(--sd-muted); margin: 0; }
.sdl-count { background: var(--sd-accent-soft); color: var(--sd-accent); border-radius: 999px; padding: 0.15rem 0.6rem; font-size: 0.8rem; font-weight: 700; }

.sdl-loading, .sdl-empty { padding: 2.5rem 1rem; text-align: center; color: var(--sd-muted); background: #fff; border: 1px dashed var(--sd-border); border-radius: var(--sd-radius); }
.sdl-empty--inline { padding: 1rem; border: 0; }
.sdl-spinner { display: inline-block; width: 28px; height: 28px; border: 3px solid var(--sd-border); border-top-color: var(--sd-accent); border-radius: 50%; animation: sdl-spin 0.8s linear infinite; }
@keyframes sdl-spin { to { transform: rotate(360deg); } }

/* Pathway cards */
.sdl-pathways { display: flex; flex-direction: column; gap: 0.85rem; }
.sdl-pathway { background: #fff; border: 1px solid var(--sd-border); border-left: 4px solid var(--sd-accent); border-radius: var(--sd-radius); box-shadow: var(--sd-shadow); overflow: hidden; }
.sdl-pathway__head { display: flex; align-items: center; gap: 1rem; width: 100%; text-align: left; background: transparent; border: 0; padding: 1.1rem 1.2rem; cursor: pointer; }
.sdl-pathway__icon { width: 44px; height: 44px; border-radius: 12px; background: var(--sd-accent-soft); color: var(--sd-accent); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sdl-pathway__main { flex: 1; min-width: 0; }
.sdl-pathway__titlerow { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.sdl-pathway__title, .sdl-course__title { font-size: 1rem; font-weight: 700; color: var(--sd-navy); margin: 0; }
.sdl-pathway__sub { font-size: 0.85rem; color: var(--sd-muted); margin: 0.2rem 0 0; }
.sdl-pathway__chev { color: var(--sd-muted); transition: transform 0.15s ease; flex-shrink: 0; }
.sdl-pathway.is-open .sdl-pathway__chev { transform: rotate(180deg); }
.sdl-kind { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--sd-accent); background: var(--sd-accent-soft); border-radius: 4px; padding: 0.1rem 0.4rem; }
.sdl-kind--course { color: #0f766e; background: #ccfbf1; }

.sdl-progress { display: flex; align-items: center; gap: 0.75rem; margin-top: 0.6rem; }
.sdl-progress__bar { flex: 1; height: 8px; border-radius: 999px; background: #f1f5f9; overflow: hidden; max-width: 320px; }
.sdl-progress__fill { display: block; height: 100%; background: var(--sd-accent); transition: width 0.3s ease; }
.sdl-progress__text { font-size: 0.78rem; color: var(--sd-muted); white-space: nowrap; }

/* Modules inside a pathway */
.sdl-modules { border-top: 1px solid var(--sd-border); background: #f8fafc; padding: 0.75rem 1.2rem 0.9rem 1.2rem; }
.sdl-module-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.4rem; }
.sdl-module { display: flex; align-items: center; gap: 0.8rem; background: #fff; border: 1px solid var(--sd-border); border-radius: 9px; padding: 0.6rem 0.8rem; }
.sdl-module--locked { opacity: 0.7; }
/* Completed, then taken out of the pathway: still the learner's, but no
   longer part of what the pathway asks of them. */
.sdl-module--retired { border-style: dashed; }
.sdl-module__num { width: 28px; height: 28px; border-radius: 50%; background: #f1f5f9; color: var(--sd-muted); display: inline-flex; align-items: center; justify-content: center; font-size: 0.8rem; font-weight: 700; flex-shrink: 0; }
.sdl-module--completed .sdl-module__num { background: #dcfce7; color: #15803d; }
.sdl-module--in_progress .sdl-module__num { background: var(--sd-accent-soft); color: var(--sd-accent); }
.sdl-module__body { flex: 1; min-width: 0; }
.sdl-module__name { font-size: 0.9rem; font-weight: 600; color: var(--sd-text); }
.sdl-module__meta { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; font-size: 0.75rem; color: var(--sd-muted); margin-top: 0.15rem; }
.sdl-module__hint { color: #94a3b8; }
.sdl-dot { color: #cbd5e1; }
.sdl-module__actions { display: flex; gap: 0.4rem; flex-shrink: 0; }
.sdl-modules__foot { margin-top: 0.6rem; text-align: right; }
.sdl-link { font-size: 0.8rem; font-weight: 600; color: var(--sd-accent); text-decoration: none; }
.sdl-link:hover { color: var(--sd-accent-dark); text-decoration: underline; }

/* Pills + buttons */
.sdl-pill { display: inline-block; padding: 0.15rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 600; white-space: nowrap; }
.sdl-pill--green { background: #dcfce7; color: #15803d; }
.sdl-pill--blue  { background: var(--sd-accent-soft); color: var(--sd-accent); }
.sdl-pill--gray  { background: #f1f5f9; color: var(--sd-muted); }
.sdl-pill--red   { background: #fee2e2; color: #b91c1c; }
.sdl-pill--amber { background: #fef3c7; color: #b45309; }
.sdl-btn { display: inline-flex; align-items: center; padding: 0.55rem 0.95rem; border-radius: 9px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; border: 1px solid var(--sd-border); background: #fff; color: var(--sd-text); line-height: 1; }
.sdl-btn:hover { border-color: var(--sd-accent); color: var(--sd-accent); }
.sdl-btn--sm { padding: 0.4rem 0.75rem; }
.sdl-btn--primary { background: var(--sd-accent); border-color: var(--sd-accent); color: #fff; }
.sdl-btn--primary:hover { background: var(--sd-accent-dark); border-color: var(--sd-accent-dark); color: #fff; }

@media (max-width: 640px) {
  .sdl-pathway__head, .sdl-module { flex-wrap: wrap; }
  .sdl-progress__bar { max-width: none; }
}
</style>
